/**
 * Data to sentences, for the parts of the ledger the read screen shows.
 * With a model attached these would be spoken in a voice; without one,
 * a plain rendering. Nothing here is state.
 */

import type { EntityRef, ObligationSubstance, Proposition, PropositionValue, TimeRef, WorldState } from '@sim/index.ts';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function nameOf(state: WorldState, ref: EntityRef): string {
  switch (ref.kind) {
    case 'player':
      return 'you';
    case 'npc':
      return state.npcs[ref.id]?.name.split(' ')[0] ?? 'someone';
    case 'location':
      return state.locations[ref.id]?.name ?? 'somewhere';
    case 'faction':
      return state.factions[ref.id]?.name ?? 'some crew';
    case 'secret':
      return 'something';
    case 'event':
      return 'what happened';
    case 'situation':
      return 'the situation';
  }
}

function objectOf(state: WorldState, o: EntityRef | PropositionValue): string {
  switch (o.kind) {
    case 'money':
      return `$${o.amount}`;
    case 'event_type':
      return o.type.replace('_', ' ');
    case 'proposition':
      return describeProposition(state, o.proposition);
    default:
      return nameOf(state, o);
  }
}

function whenOf(t: TimeRef | null): string {
  if (t === null) return '';
  if (t.kind === 'day') return ` on ${WEEKDAYS[((t.day % 7) + 7) % 7]}`;
  return ` between ${WEEKDAYS[((t.from % 7) + 7) % 7]} and ${WEEKDAYS[((t.to % 7) + 7) % 7]}`;
}

export function describeProposition(state: WorldState, p: Proposition): string {
  const s = nameOf(state, p.subject);
  const o = objectOf(state, p.object);
  const w = whenOf(p.timeRef);
  const cap = s.charAt(0).toUpperCase() + s.slice(1);
  switch (p.predicate) {
    case 'was_at':
      return `${cap} ${s === 'you' ? 'were' : 'was'} at ${o}${w}.`;
    case 'did':
      return `${cap} ${o}${w}.`;
    case 'owes':
      return `${cap} owe${s === 'you' ? '' : 's'} ${o}${w}.`;
    case 'works_for':
      return `${cap} work${s === 'you' ? '' : 's'} for ${o}.`;
    case 'bonded_to':
      return `${cap} ${s === 'you' ? 'are' : 'is'} close to ${o}.`;
    case 'moved_against':
      return `${cap} moved against ${o}${w}.`;
    case 'will_be_at':
      return `${cap} will be at ${o}${w}.`;
    case 'is_dead':
      return `${cap} ${s === 'you' ? 'are' : 'is'} dead${w}.`;
    case 'said':
      return `${cap} said: ${o}`;
    case 'knows':
      return `${cap} know${s === 'you' ? '' : 's'} about ${o}.`;
  }
}

export function describeSubstance(s: ObligationSubstance): string {
  return s.kind === 'money' ? `$${s.amount}` : s.description;
}
